// Matches the start of any top-level TQL statement, grounded in the grammar reference.
// Built with RegExp constructor so the alternatives can be formatted readably.
// Flags: i = case-insensitive, m = ^ matches start of any line.
const TQL_STATEMENT_START = new RegExp(
    "^\\s*(" +
        [
            // CREATE [OR REPLACE] [JUMPING|SESSION|EXTERNAL|OPEN] <entity>
            "CREATE(\\s+OR\\s+REPLACE)?(\\s+(JUMPING|SESSION|EXTERNAL|OPEN))?\\s+" +
                "(SOURCE|TARGET|STREAM|FLOW|APPLICATION|CQ|WINDOW|TYPE|CACHE|WACTIONSTORE|" +
                "SUBSCRIPTION|EVENTTABLE|PROPERTYSET|PROPERTYVARIABLE|CONNECTIONPROFILE|" +
                "VAULT|ROUTER|SORTER|SENTINEL|EMBEDDINGGENERATOR|NAMESPACE|USER|ROLE|DG|" +
                "EXCEPTIONSTORE|NAMEDQUERY|DASHBOARD|VISUALIZATION|PROCESSOR|GROUP)",

            // ALTER — every form documented in the grammar + per-component notes
            "ALTER\\s+(APPLICATION|FLOW|NAMEDQUERY|DG|PROPERTYSET|EXCEPTIONSTORE)",

            // DROP — every entity-kind listed in the grammar (incl. AIAGENT)
            "DROP\\s+(SOURCE|TARGET|STREAM|CQ|WINDOW|FLOW|APPLICATION|TYPE|CACHE|WACTIONSTORE|" +
                "SUBSCRIPTION|EVENTTABLE|PROPERTYSET|PROPERTYVARIABLE|CONNECTIONPROFILE|VAULT|" +
                "ROUTER|SORTER|SENTINEL|EMBEDDINGGENERATOR|NAMESPACE|USER|ROLE|DG|EXCEPTIONSTORE|" +
                "NAMEDQUERY|DASHBOARD|AIAGENT)",

            // Application lifecycle
            "END\\s+(APPLICATION|FLOW)",
            "DEPLOY\\s+APPLICATION",
            "UNDEPLOY\\s+APPLICATION",
            "(START|STOP|RESUME|QUIESCE|CHECKPOINT)\\s+APPLICATION",
            "(LOAD|UNLOAD)\\s+(SOURCE|CACHE|WACTIONSTORE)",

            // Namespace switch
            "USE\\s+(GLOBAL|\\w+)",

            // Security
            "GRANT\\b",
            "REVOKE\\b",

            // Vault operators (WRITE INTO / READ [ALL] FROM / DELETE FROM)
            "WRITE\\s+INTO",
            "READ(\\s+ALL)?\\s+FROM",
            "DELETE\\s+FROM",
        ].join("|") +
        ")",
    "im"
);

// Returns true only when a line is *obviously* natural-language prose, not TQL.
// When in doubt (e.g. a truncated identifier, a bare property key, a partial value)
// we return false so the caller keeps the line in the TQL block.
function isObviouslyProse(trimmed: string): boolean {
    // Empty lines are neutral — caller handles them separately.
    if (!trimmed) return false;

    // A single token with no whitespace is ambiguous: it could be a truncated property
    // key, a bare identifier, or a partial value. Never treat it as prose.
    if (!/\s/.test(trimmed)) return false;

    // TQL structural characters, statement keywords, property patterns — definitely TQL.
    if (/^[();]/.test(trimmed)) return false;
    if (/^(CREATE|ALTER|DROP|END|USE|DEPLOY|UNDEPLOY|START|STOP|RESUME|QUIESCE|CHECKPOINT|LOAD|UNLOAD|GRANT|REVOKE|WRITE|READ|DELETE|INSERT|SELECT|FROM|WHERE|GROUP|HAVING|ORDER|USING|PARSE|FORMAT|PARTITION|GRACE|PERSIST|OUTPUT|INPUT|RECOVERY|WITH|MATCH_PATTERN|DEFINE|MODIFY|SAMPLE|LINK|ENRICH|DETECTION|OVER|KEEP|WITHIN|SLIDE|RANGE|IDLE|QUERY|CONTEXT|EVENT|CASE|WHEN|ELSE|ROUTE)\b/i.test(trimmed))
        return false;
    if (/^[A-Za-z_]\w*\s*[:=(]/.test(trimmed)) return false; // Key: value or Key = value
    if (trimmed.endsWith(",")) return false; // property list continuation
    if (trimmed.startsWith("--")) return false; // TQL comment
    if (/^['"]/.test(trimmed)) return false; // string literal value

    // Multiple words ending like a sentence — clearly prose.
    if (/[.?!]$/.test(trimmed) && trimmed.split(/\s+/).length >= 3) return true;

    // Starts with a word that can never begin a TQL line.
    if (/^(i\s|me\s|my\s|we\s|please\s|just\s|thanks\b|btw\s|hi\s|hello\s)/i.test(trimmed)) return true;

    // Everything else is ambiguous — keep it in the TQL block.
    return false;
}

export interface ExtractedTQL {
    prefix: string;
    tql: string;
    suffix: string;
    hasMixedContent: boolean;
}

export function extractTQL(text: string): ExtractedTQL {
    const noTQL: ExtractedTQL = { prefix: "", tql: text, suffix: "", hasMixedContent: false };

    if (!TQL_STATEMENT_START.test(text)) return noTQL;

    const lines = text.split("\n");
    let startLine = -1;

    for (let i = 0; i < lines.length; i++) {
        if (TQL_STATEMENT_START.test(lines[i])) {
            startLine = i;
            break;
        }
    }

    if (startLine === -1) return noTQL;

    // Walk forward; stop only when we're certain we've hit natural-language prose.
    // Blank lines, truncated identifiers, and ambiguous tokens are all kept in the block.
    let endLine = startLine;
    for (let i = startLine; i < lines.length; i++) {
        const trimmed = lines[i].trim();
        if (!trimmed) continue; // blank line — don't advance endLine, keep scanning
        if (isObviouslyProse(trimmed)) break;
        endLine = i;
    }

    // Prefix: lines before TQL block, trailing blank lines stripped
    const prefixLines = lines.slice(0, startLine);
    while (prefixLines.length > 0 && prefixLines[prefixLines.length - 1].trim() === "")
        prefixLines.pop();

    // Suffix: lines after TQL block, leading blank lines stripped
    const suffixLines = lines.slice(endLine + 1);
    while (suffixLines.length > 0 && suffixLines[0].trim() === "") suffixLines.shift();

    const prefix = prefixLines.join("\n");
    const tql = lines.slice(startLine, endLine + 1).join("\n");
    const suffix = suffixLines.join("\n");

    return {
        prefix,
        tql,
        suffix,
        hasMixedContent: prefix.trim() !== "" || suffix.trim() !== "",
    };
}

export function reconstructMessage(extracted: Pick<ExtractedTQL, "prefix" | "suffix">, tql: string): string {
    return [extracted.prefix, tql, extracted.suffix]
        .filter(p => p.trim() !== "")
        .join("\n\n");
}

export { TQL_STATEMENT_START as TQL_PATTERN };
